











































import { computed, defineComponent, ref } from "@vue/composition-api";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { useBecomeCreatorState } from "@/hooks/useBecomeCreatorState";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseIconButton from "@/components/BaseIconButton";
import BaseSpinner from "@/components/BaseSpinner";

type VisibleState =
  | "limitedCta"
  | "limitedInProgress"
  | "limitedReview"
  | "limitedReviewWithFullInProgress"
  | "limitedRejected"
  | "fullCta"
  | "fullInProgress"
  | "fullReview"
  | "fullRejected"
  | null;

type Parts = {
  reviewText?: string;
  rejectedTitle?: string;
  rejectedReason?: string | null;
  inProgressText?: string;
  ctaText?: string;
};

export default defineComponent({
  name: "BecomeCreatorStatus",
  components: { BaseSpinner, BaseIconButton, BaseIcon, BaseButton },
  setup() {
    const { userVerification } = useAuthUser();
    const { lastStep, getData, update } = useBecomeCreatorState();

    const loading = ref(true);

    const visibleState = computed<VisibleState>(() => {
      const uv = userVerification.value;
      if (!uv) return null;

      const inProgress = lastStep.value > 1;

      if (uv.upgradeStatus === UserVerificationStatus.VERIFIED) return null;
      if (uv.upgradeStatus === UserVerificationStatus.WAITING_FOR_APPROVAL) return "fullReview";

      if (uv.upgradeStatusStartTime) {
        if (uv.restrictUpgrade) return null;
        if (!uv.upgradeStatusDeclineReason) return "fullCta";
        return inProgress ? "fullInProgress" : "fullRejected";
      }

      if (uv.startTime) {
        if (uv.status === UserVerificationStatus.WAITING_FOR_APPROVAL) {
          return inProgress ? "limitedReviewWithFullInProgress" : "limitedReview";
        }
        if (uv.status === UserVerificationStatus.VERIFIED) {
          if (uv.restrictUpgrade) return null;
          return inProgress ? "fullInProgress" : "fullCta";
        }
        return inProgress ? "limitedInProgress" : "limitedRejected";
      }

      if (inProgress) return "limitedInProgress";
      if (!config.featureSoloModel && !config.featureHideBecomeCreator) return "limitedCta";
      return null;
    });

    const parts = computed<Parts>(() => {
      switch (visibleState.value) {
        case "limitedCta":
          return { ctaText: $t("Become a Creator") };
        case "limitedInProgress":
          return { inProgressText: $t("Limited Creator application in progress...") };
        case "limitedReview":
          return {
            reviewText: $t("Limited Creator application under review"),
            ctaText: $t("Become a Full Creator"),
          };
        case "limitedReviewWithFullInProgress":
          return {
            reviewText: $t("Limited Creator application under review"),
            inProgressText: $t("Full Creator account application in progress..."),
          };
        case "limitedRejected":
          return {
            rejectedTitle: $t("Identity verification for your application has been rejected. Please try again."),
          };
        case "fullCta":
          return { ctaText: $t("Become a Full Creator") };
        case "fullInProgress":
          return { inProgressText: $t("Full Creator account application in progress...") };
        case "fullReview":
          return { reviewText: $t("Full Creator application under review") };
        case "fullRejected":
          return {
            rejectedTitle: $t("Full Creator application has been rejected."),
            rejectedReason: userVerification.value?.upgradeStatusDeclineReason ?? null,
          };
        default:
          return {};
      }
    });

    const reset = () => update(0);

    getData().finally(() => (loading.value = false));

    return {
      loading,
      visibleState,
      parts,
      reset,
    };
  },
});
